/**
 * Email Verification Banner
 *
 * Displays a banner prompting users to verify their email address.
 * Shows for users who signed up with email/password and haven't verified yet.
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EnvelopeIcon, XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';

export const EmailVerificationBanner = () => {
  const { user, refreshUserVerificationStatus, resendVerificationEmail } = useAuth();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Listen for successful verification and reload user
  useEffect(() => {
    // Check if we just came back from verification page
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('verified') === 'true' && user && !user.emailVerified) {
      // Email was just verified, refresh user verification status
      console.log('✨ User just completed email verification, refreshing status...');
      refreshUserVerificationStatus();
    }
  }, [location.search, user, refreshUserVerificationStatus]);

  // Don't show if user is verified, not signed in, banner dismissed, or on verification page
  if (!user || user.emailVerified || dismissed || location.pathname === '/verify-email') {
    return null;
  }

  // Only show for email/password users (not Google sign-in)
  const isEmailPasswordUser = user.providerData.some(
    (provider) => provider.providerId === 'password'
  );

  if (!isEmailPasswordUser) {
    return null;
  }

  const handleResend = async () => {
    try {
      setSending(true);
      setError(null);
      await resendVerificationEmail();
      setSent(true);
      setTimeout(() => setSent(false), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to send verification email');
      setTimeout(() => setError(null), 5000);
    } finally {
      setSending(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="sticky top-0 left-0 right-0 z-40 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-white shadow-lg"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Left: Icon and Message */}
            <div className="flex items-center gap-3 flex-1">
              <EnvelopeIcon className="w-6 h-6 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">
                  Please verify your email address
                </p>
                <p className="text-xs text-white/90 mt-0.5">
                  Check your inbox for a verification link. Can't find it? Check your spam folder.
                </p>
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Success Message */}
              {sent && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-lg"
                >
                  <CheckCircleIcon className="w-4 h-4" />
                  <span className="text-xs font-medium">Email sent!</span>
                </motion.div>
              )}

              {/* Error Message */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center gap-2 px-3 py-1.5 bg-red-500/20 rounded-lg"
                >
                  <ExclamationTriangleIcon className="w-4 h-4" />
                  <span className="text-xs font-medium">{error}</span>
                </motion.div>
              )}

              {/* Resend Button */}
              {!sent && !error && (
                <button
                  onClick={handleResend}
                  disabled={sending}
                  className="px-4 py-1.5 text-sm font-semibold bg-white/20 hover:bg-white/30 disabled:bg-white/10 disabled:cursor-not-allowed rounded-lg transition-colors backdrop-blur-sm border border-white/30"
                >
                  {sending ? 'Sending...' : 'Resend Email'}
                </button>
              )}

              {/* Dismiss Button */}
              <button
                onClick={handleDismiss}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Dismiss"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
